import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

// Dark product card with cursor-following accent spotlight + light product plate.
export default function ProductCard({ product, index = 0 }) {
  const { t } = useLanguage();
  const ref = useRef(null);

  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty("--mx", `${e.clientX - r.left}px`);
    el.style.setProperty("--my", `${e.clientY - r.top}px`);
  };

  return (
    <Link
      ref={ref}
      to={`/products/${product.slug}`}
      onMouseMove={onMove}
      data-testid={`product-card-${product.slug}`}
      className="group relative flex h-full flex-col overflow-hidden border border-black/10 bg-card transition-colors duration-300 hover:border-primary/40"
    >
      {/* spotlight */}
      <span
        className="pointer-events-none absolute inset-0 z-10 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
        style={{
          background:
            "radial-gradient(340px circle at var(--mx) var(--my), hsl(var(--primary) / 0.16), transparent 70%)",
        }}
      />
      {/* image plate */}
      <div className="relative overflow-hidden bg-gradient-to-b from-neutral-100 to-neutral-300 p-8">
        <span className="label absolute left-4 top-4 z-10 text-neutral-500">{product.code}</span>
        <img
          src={product.image}
          alt={product.name}
          loading="lazy"
          className="mx-auto h-52 w-auto object-contain drop-shadow-xl transition-transform duration-500 ease-out group-hover:scale-105"
        />
      </div>
      {/* body */}
      <div className="relative z-10 flex flex-1 flex-col p-6">
        <div className="flex-1">
          <span className="label text-primary">{product.classification}</span>
          <h3 className="mt-3 font-display text-xl font-semibold leading-tight">{product.name}</h3>
          <p className="mt-2 text-sm text-muted-foreground">{product.tagline}</p>
        </div>
        <div className="mt-6 flex items-center justify-between border-t border-black/10 pt-4">
          <span className="text-xs text-muted-foreground">{product.packaging[0]}</span>
          <span className="inline-flex items-center gap-1 text-xs font-medium text-foreground transition-colors group-hover:text-primary">
            {t("products.explore")}
            <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:rotate-45" />
          </span>
        </div>
      </div>
    </Link>
  );
}
